import React from 'react';
import { Vote, LogIn, Film } from 'lucide-react';
import { VOTE_OPTIONS } from '../../config/supabase';
import MoviePoster from '../viewer/MoviePoster';
import { useLazyMovieData } from '../../hooks/useLazyTMDB';

// Individual movie vote card component with lazy loading
const MovieVoteCard = ({ movie, category, userVotes, onVote, isAuthenticated, isSurveyClosed, onMovieClick }) => {
  const { movieData, elementRef } = useLazyMovieData(movie, { immediate: false });
  
  return (
    <div ref={elementRef} key={movie.id} className="bg-gray-700 border border-orange-500 rounded-lg overflow-hidden">
      {/* Movie Poster/Header */}
      <div className="relative">
        <MoviePoster
          movie={movie}
          size="md"
          className="w-full h-48 object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <h3 className="font-bold text-white text-lg">{movieData.title}</h3>
          <p className="text-gray-300 text-sm">
            {movieData.year} • {movieData.runtime}min
          </p>
        </div>
      </div>

      {/* Movie Details */}
      <div className="p-4">
        {movieData.plot && (
          <p className="text-gray-300 text-sm mb-4 line-clamp-3">
            {movieData.plot}
          </p>
        )}

        {/* Voting Buttons */}
        <div className="space-y-2">
          <div className="text-orange-300 font-semibold text-sm mb-2">
            Your Vote:
          </div>
          {VOTE_OPTIONS.map(option => (
            <button
              key={option.value}
              onClick={() => onVote(category.id, movie.id, option.value)}
              disabled={!isAuthenticated || isSurveyClosed}
              className={`w-full text-left px-3 py-2 rounded-lg transition-colors text-sm flex items-center gap-2 ${
                userVotes[movie.id] === option.value
                  ? 'bg-orange-600 text-white border-2 border-orange-400'
                  : isAuthenticated && !isSurveyClosed
                    ? 'bg-gray-600 text-gray-200 hover:bg-gray-500 border border-gray-500'
                    : 'bg-gray-800 text-gray-500 cursor-not-allowed border border-gray-700'
              }`}
            >
              <span className="text-base">{option.emoji}</span>
              <span className="flex-1">{option.label}</span>
              {userVotes[movie.id] === option.value && (
                <div className="w-2 h-2 bg-white rounded-full"></div>
              )}
            </button>
          ))}
        </div>

        {/* Current Vote Display */}
        {userVotes[movie.id] !== undefined && (
          <div className="mt-3 p-2 bg-orange-900 bg-opacity-30 border border-orange-600 rounded text-center">
            <div className="text-orange-300 text-xs font-semibold">Your Current Vote:</div>
            <div className="text-orange-200 text-sm">
              {VOTE_OPTIONS.find(opt => opt.value === userVotes[movie.id])?.emoji}{' '}
              {VOTE_OPTIONS.find(opt => opt.value === userVotes[movie.id])?.label}
            </div>
          </div>
        )}

        {/* Movie Details Button */}
        {onMovieClick && (
          <button
            onClick={() => onMovieClick(movie)}
            className="mt-3 w-full bg-blue-600 hover:bg-blue-500 text-white px-3 py-2 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
          >
            <Film size={16} />
            Movie Details
          </button>
        )}
      </div>
    </div>
  );
};

const VotingInterface = ({ 
  category, 
  movies, 
  userVotes, 
  onVote,
  isAuthenticated, 
  currentUser,
  onMovieClick
}) => {
  const getMovieById = (movieId) => {
    // Handle both string and number IDs by converting both to strings for comparison
    const searchId = String(movieId);
    return movies.find(movie => String(movie.id) === searchId);
  };

  // Parse movie_ids if it's a JSON string, otherwise use as array
  let movieIds = [];
  if (Array.isArray(category.movie_ids)) {
    movieIds = category.movie_ids;
  } else if (typeof category.movie_ids === 'string') {
    try {
      movieIds = JSON.parse(category.movie_ids);
    } catch (error) {
      console.warn('Failed to parse movie_ids JSON string:', error);
      movieIds = [];
    }
  }
  const categoryMovies = movieIds.map(getMovieById).filter(Boolean) || [];
  const isSurveyClosed = category.is_closed;


  // Show loading state if category content is not yet loaded
  if (!category?.movie_ids || movieIds.length === 0) {
    console.log('🔄 VotingInterface showing loading state:', {
      categoryName: category?.name,
      hasMovieIds: !!category?.movie_ids,
      movieIdsLength: movieIds.length,
      movieIdsType: typeof category?.movie_ids
    });
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-400 mx-auto mb-4"></div>
        <h3 className="text-xl font-semibold text-gray-400 mb-2">Loading Category Content...</h3>
        <p className="text-gray-500">
          Fetching movies for this category...
        </p>
      </div>
    );
  }

  if (categoryMovies.length === 0) {
    return (
      <div className="text-center py-12">
        <Film className="w-16 h-16 mx-auto text-gray-500 mb-4" />
        <h3 className="text-xl font-semibold text-gray-400 mb-2">No Movies in Category</h3>
        <p className="text-gray-500">
          This category doesn't have any movies yet.
        </p>
      </div>
    );
  }

  console.log('✅ VotingInterface rendering content:', {
    categoryName: category?.name,
    movieCount: categoryMovies.length,
    hasMovieIds: !!category?.movie_ids,
    movieIdsLength: movieIds.length
  });

  return (
    <div className="space-y-6">
      {/* Authentication Notice */}
      {!isAuthenticated && (
        <div className="bg-blue-900 border border-blue-600 rounded-lg p-4 flex items-center justify-center">
          <div className="text-center">
            <div className="flex items-center justify-center mb-3">
              <LogIn className="text-blue-400" size={20} />
            </div>
            <div className="text-blue-200 font-semibold mb-2">Sign in to vote</div>
            <div className="text-blue-300 text-sm">
              Use the "Log in" button in the top right corner to join this survey
            </div>
          </div>
        </div>
      )}

      {/* Survey Closed Notice */}
      {isSurveyClosed && (
        <div className="bg-red-900 border border-red-600 rounded-lg p-4 flex items-center gap-3">
          <div className="text-red-400 text-2xl">🔒</div>
          <div>
            <div className="text-red-200 font-semibold">Survey Closed</div>
            <div className="text-red-300 text-sm">
              Voting has been closed for this survey. You can still view the results.
            </div>
          </div>
        </div>
      )}

      {/* User Info */}
      {isAuthenticated && currentUser && (
        <div className="bg-green-900 border border-green-600 rounded-lg p-3 flex items-center gap-3">
          <div className="text-green-300 text-sm">
            👤 Voting as: <strong>{currentUser.name}</strong>
          </div>
        </div>
      )}

      {/* Voting Instructions */}
      <div className="bg-gray-700 border border-orange-500 rounded-lg p-4">
        <h3 className="text-orange-300 font-semibold mb-2 flex items-center gap-2">
          <Vote size={16} />
          How to Vote:
        </h3>
        <p className="text-gray-300 text-sm mb-3">
          Rate each movie based on how much you want to see it. The movies with the highest ratings will make the cut, along with a few surprises (and as always, your host reserves the right to fudge the results if the categories get too skewed).
        </p>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-2 text-xs">
          {VOTE_OPTIONS.map(option => (
            <div key={option.value} className="flex items-center gap-2">
              <span className="text-lg">{option.emoji}</span>
              <span className={option.color}>{option.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Movies Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {categoryMovies.map(movie => (
          <MovieVoteCard
            key={movie.id}
            movie={movie}
            category={category}
            userVotes={userVotes}
            onVote={onVote}
            isAuthenticated={isAuthenticated}
            isSurveyClosed={isSurveyClosed}
            onMovieClick={onMovieClick}
          />
        ))}
      </div>

      {/* Vote Summary */}
      {isAuthenticated && Object.keys(userVotes).length > 0 && (
        <div className="bg-green-900 border border-green-600 rounded-lg p-4">
          <h3 className="text-green-300 font-semibold mb-2">Your Votes Summary:</h3>
          <div className="text-green-200 text-sm">
            You have voted on {Object.keys(userVotes).length} out of {categoryMovies.length} movies in this category.
            {Object.keys(userVotes).length < categoryMovies.length && (
              <span className="block mt-1 text-green-300">
                {categoryMovies.length - Object.keys(userVotes).length} movies remaining.
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default VotingInterface;
